import { sanitize, useIntl } from '@capsicum/common';
import { BaseSearchInput, Button, Chip, HeaderBlock } from '@capsicum/common-web';
import { ChevronLeftBoldIcon, CrossIcon } from '@capsicum/common-web/icons';
import { type PartnerWithImageMap, usePartnerFiltered, usePartners } from '@capsicum/socios-core';
import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

export type PartnerFilterProps = PropsWithChildren<{
	closeModal: () => void;
	context?: string;
}>;

type PartnerUnion = PartnerWithImageMap & { selected: boolean };

/**
 * Filter by partners.
 * @param {PartnerFilterProps} props Component's props
 * @param {string} props.context The context/page in which the partner filter will be rendered
 * @param {function} props.closeModal Closes the drawer
 * @returns {JSX.Element} The partner filter component
 */

export const PartnerFilter = ({ closeModal, context, ...props }: PartnerFilterProps) => {
	const { $t } = useIntl();
	const { getPartnerBy, partners: allPartners } = usePartners();
	const { getPartnersFilteredByContext, setPartnersFilteredByContext } = usePartnerFiltered();
	const [searchText, setSearchText] = useState<string>('');
	const [selectedPartners, setSelectedPartners] = useState<Map<string, PartnerUnion>>(new Map());
	const partnersFromState = getPartnersFilteredByContext(context || 'default');

	const isButtonDisabled =
		selectedPartners.size === partnersFromState.length &&
		[...selectedPartners.keys()].every(key => partnersFromState.some(partnerId => key === partnerId));

	useEffect(() => {
		const partnersSelected: Array<[string, PartnerUnion]> = partnersFromState.map((partnerId: string) => [
			partnerId,
			{
				...getPartnerBy(p => p.id === partnerId),
				selected: true
			} as PartnerUnion
		]);

		return setSelectedPartners(new Map(partnersSelected));
	}, [partnersFromState, getPartnerBy]);

	const partners = useMemo(
		() =>
			(allPartners ?? []).map(partner => ({
				...partner,
				selected: !!(partner.id && selectedPartners.has(partner.id))
			})),
		[allPartners, selectedPartners]
	);

	const onChipRemove = useCallback(
		(value: PartnerUnion) => {
			const selectedPartnersCopy = new Map(selectedPartners);

			selectedPartnersCopy.delete(value.id!);

			setSelectedPartners(selectedPartnersCopy);
		},
		[selectedPartners]
	);

	const onChipSelect = useCallback(
		(value: PartnerUnion) => {
			if (selectedPartners.has(value.id!)) {
				onChipRemove(value);
			} else {
				setSelectedPartners(x => new Map(x.set(value.id!, value)));
			}
		},
		[onChipRemove, selectedPartners]
	);

	const onValidate = () => {
		setPartnersFilteredByContext(context!, [...selectedPartners.keys()]);
		closeModal?.();
	};

	const onBackButton = () => {
		closeModal?.();
	};

	const onClear = () => {
		setSelectedPartners(new Map());
	};

	const filteredPartners = useMemo(() => {
		if (searchText === '') {
			return partners.filter(partner => partner.isSigned);
		} else {
			return partners?.filter(
				partner =>
					partner.isSigned &&
					(sanitize(partner.shortName).includes(sanitize(searchText)) ||
						sanitize(partner.name).includes(sanitize(searchText)))
			);
		}
	}, [partners, searchText]);

	const renderItem = useCallback(
		(item: PartnerUnion) => {
			return (
				item &&
				((
					<S.StyledChip
						imageStart={item?.images?.partnerLogo?.url}
						isSelected={item.selected}
						key={item.id + '-list'}
						testID={`partner-filter-chip-${item.id}`}
						text={item.shortName}
						value={item.id!}
						onClick={() => onChipSelect(item)}
					/>
				) as JSX.Element)
			);
		},
		[onChipSelect]
	);

	const renderSelectedChips = useMemo(() => {
		return Array.from(selectedPartners.entries()).map(([partnerId, partner]) => {
			return (
				<S.StyledChip
					iconEnd={S.StyledCrossIcon}
					imageStart={partner?.images?.partnerLogo?.url}
					isSelected
					key={partnerId + '-chip'}
					testID={`partner-filter-selectedChip-${partner?.id}`}
					text={partner?.shortName || ''}
					value=""
					variant="brand"
					onClick={() => onChipRemove(partner)}
				/>
			);
		});
	}, [selectedPartners, onChipRemove]);

	return (
		<S.Wrapper>
			<S.PartnerFilterWrapper {...props}>
				<S.BackHeaderBlock>
					<S.BackButton onClick={() => onBackButton()}>
						<ChevronLeftBoldIcon />
					</S.BackButton>
					<S.StyledHeaderBlock
						actionText={$t('rewards.filters.clear')}
						testID="partner-filter-header"
						title={$t('global.filter')}
						onActionClick={selectedPartners.size ? onClear : undefined}
					/>
				</S.BackHeaderBlock>
				<S.StyledSearchInput
					debounceTime={250}
					placeholder={$t('selectFavouriteTeam.search')}
					testID="partner-filter-searchInput-text"
					onChange={e => setSearchText(e.target.value)}
					onClear={() => setSearchText('')}
				/>
				<S.SelectedPartners>
					<S.StyledChipClear
						isSelected={!renderSelectedChips.length}
						testID="partner-filter-selectedChip-clearAll"
						text={$t('rewards.filters.allChip')}
						value="All"
						onClick={onClear}
					/>
					{selectedPartners.size > 0 && !!renderSelectedChips.length && renderSelectedChips}
				</S.SelectedPartners>
				<S.StyledPartnerList>
					{filteredPartners?.map(item => {
						return renderItem(item);
					})}
				</S.StyledPartnerList>
				<S.ButtonWrapper>
					<Button
						color="brand"
						disabled={isButtonDisabled}
						fullWidth
						testID="partner-filter-button-seeResult"
						text={$t('rewards.filters.seeResult')}
						onClick={() => onValidate()}
					/>
				</S.ButtonWrapper>
			</S.PartnerFilterWrapper>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex-direction: column;
		height: 100%;
		justify-content: space-between;
	`;

	export const PartnerFilterWrapper = styled.div`
		display: flex;
		flex-direction: column;
		height: 100%;
	`;

	export const StyledHeaderBlock = styled(HeaderBlock)`
		flex: 1;
	`;

	export const BackHeaderBlock = styled.div`
		display: flex;
	`;

	export const BackButton = styled.button`
		height: fit-content;
		text-decoration: none;
		border: none;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		background: ${({ theme }) => theme.colors.backgrounds.transparent};
	`;

	export const StyledPartnerList = styled.div`
		display: flex;
		flex-wrap: wrap;
		overflow: auto;
		margin-left: ${({ theme }) => `-${theme.spacings.xsmall}`};
		align-content: flex-start;
	`;

	export const SelectedPartners = styled.div`
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		margin: ${({ theme }) => theme.spacings.xsmall} 0;
		margin-left: ${({ theme }) => `-${theme.spacings.xsmall}`};
	`;

	export const ChipWrapper = styled.div`
		padding-right: ${({ theme }) => theme.spacings.xsmall};
		padding-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const ButtonWrapper = styled.div`
		justify-content: center;
		display: flex;
		${({ theme }) => theme.border.radius.circle};
		margin: ${({ theme }) => `${theme.spacings.xsmall} 0`};
		width: 100%;
		flex: 0;
	`;

	export const StyledCrossIcon = styled(CrossIcon)`
		color: ${({ theme }) => theme.colors.icons.brand.enabled};
	`;

	export const StyledCrossClearIcon = styled(CrossIcon)`
		color: ${({ theme }) => theme.colors.icons.main.inverse};
	`;

	export const StyledChip = styled(Chip)`
		margin: ${({ theme }) => `0 0 ${theme.spacings.xsmall} ${theme.spacings.xsmall}`};
	`;

	export const StyledChipClear = styled(StyledChip)`
		margin-right: 0;
	`;

	export const StyledSearchInput = styled(BaseSearchInput)`
		${({ theme }) => theme.fonts.text.text3};
	`;
}
